import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useSchemeList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refActiveSchemesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'id',
      sortable: true,
    },
    {
      key: 'name',
      label: 'Scheme',
      sortable: true,
    },
    {
      key: 'remarks',
      label: 'Internal Remarks',
      sortable: true,
    },
    {
      key: 'conditions',
      label: 'Conditions',
      sortable: false,
    },
    {
      key: 'items',
      label: 'Entitlement items',
      sortable: false,
    },
    { key: 'status', sortable: true },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: false,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalSchemes = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'createdAt')
  const isSortDirDesc = ref(route.query.sortDesc !== undefined ? route.query.sortDesc === 'true' : true)
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }

  const toDeletedID = ref(null)
  const type = ref('active')
  const schemeData = ref([])
  const startDateFilter = ref(route.query.startDate || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refActiveSchemesTable.value ? refActiveSchemesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalSchemes.value,
    }
  })

  const refetchData = () => {
    refActiveSchemesTable.value.refresh()
  }

  let timer = null
  watch([searchQuery, statusFilter, startDateFilter], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      refetchData()
    }, 1000)
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchSchemes = (ctx, callback) => {
    store
      .dispatch('products-active-schemes/fetchSchemes', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        startDate: startDateFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { schemes, total } = response.data
        schemeData.value = schemes
        callback(schemes)
        totalSchemes.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              startDate: startDateFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteScheme = () => {
    store
      .dispatch('products-active-schemes/deleteScheme', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveSchemeStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'danger'
    if (status === 'expired') return 'secondary'
    return 'primary'
  }

  return {
    fetchSchemes,
    tableColumns,
    perPage,
    currentPage,
    totalSchemes,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refActiveSchemesTable,

    resolveSchemeStatusVariant,
    refetchData,

    toDeletedID,
    deleteScheme,
    schemeData,

    // Extra Filters
    statusFilter,
    startDateFilter,
  }
}
